import {
  Box,
  Flex,
  Text,
  Grid,
  TableContainer,
  Table,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  useToken,
} from "@chakra-ui/react";
import React, { Fragment, useState } from "react";
import PrimaryButton from "../../components/themeComponents/PrimaryButton";
import OutlinedButton from "../../components/themeComponents/OutlinedButton";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { deleteEmailTemplate, getEmailTemplateList } from "../../Services/api";
import TableHead from "../Campaigns/components/TableHead";
import TemplateTable from "./TemplateTable";
import { FaSearch } from "react-icons/fa";
import { MdOutlineZoomOutMap } from "react-icons/md";
import hexToRgba from "hex-to-rgba";
import { showSwal } from "../../_helpers/helpers";
import Swal from "sweetalert2";
import _ from "lodash";
import { OvalSpinner } from "../../components/Loaders";
import Pagination from "../../components/Pagination"; // Import the generic Pagination component

export default function EmailTemplateList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [templatePreviewBody, setTemplatePreviewBody] = useState("");
  const [mainColor] = useToken("colors", ["primary.main"]);
  const [totalCount, setTotalCount] = useState(0);
  const [preheaderText, setPreheaderText] = useState("");
  const [subjectLine, setSubjectLine] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [deletedUuid, setDeletedUuid] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const templateListQuery = useQuery(
    ["emailTemplateList", currentPage, searchKeyword, deletedUuid],
    async () => {
      const res = await getEmailTemplateList(currentPage, searchKeyword);
      setTotalCount(res?.data?.count ?? 0);
      return res.data?.items ?? [];
    },
    {
      staleTime: 0, // Ensure fresh data on page change
      cacheTime: 0,
      retry: 1,
      enabled: true, // Always enabled since no conditional dependencies
    }
  );

  const onPreview = (body, subjectLine, preHeader) => {
    setTemplatePreviewBody(body);
    setSubjectLine(subjectLine);
    setPreheaderText(preHeader);
    const iframeDesktop = document.querySelector("#iframeDesktop");
    if (iframeDesktop) {
      iframeDesktop.contentWindow.document.open("text/html", "replace");
      iframeDesktop.contentWindow.document.write(body);
      iframeDesktop.contentWindow.document.close();
    }
  };

  const onDelete = (templateUuid) => {
    showSwal(
      Swal,
      "Are you sure?",
      "Do you want to delete the template?",
      true,
      true,
      "Yes, Delete",
      "No",
      async () => {
        try {
          await deleteEmailTemplate(templateUuid);
          setDeletedUuid(templateUuid); // Trigger refetch
        } catch (error) {
          console.error("Error deleting template:", error);
        }
      },
      (res) => {
        if (res.isConfirmed) {
          Swal.close();
        } else {
          Swal.close();
        }
      }
    );
  };

  const searchCampaign = _.debounce((e) => {
    setSearchKeyword(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  }, 300);

  return (
    <Fragment>
      <Box w="100%" overflow="hidden" height="100%">
        <Flex justifyContent="space-between">
          <Text as="b" fontSize="2xl">
            Email Templates
          </Text>
          <OutlinedButton
            width="10vw"
            borderRadius="8px"
            label={t("flows.build.own")}
            onClick={() => navigate("/email/edit")}
          />
        </Flex>
        <Grid templateColumns="1fr 1fr" h="calc(100% - 50px)">
          <Box
            h="100%"
            overflow="hidden"
            bg="white"
            p={3}
            borderRight="1px solid"
            borderColor="gray.200"
          >
            <InputGroup mb={2}>
              <InputLeftElement>
                <Icon as={FaSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search email template by name"
                onChange={searchCampaign}
              />
            </InputGroup>
            <TableContainer
              mt={4}
              overflowY="auto"
              overflow="auto"
              height="calc(100% - 50px)"
            >
              <Table>
                {templateListQuery.isLoading && <OvalSpinner />}
                {templateListQuery.isError && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={10}
                  >
                    <Text color="red.500">
                      Error loading templates. Please try again.
                    </Text>
                  </Box>
                )}
                {!templateListQuery.isLoading &&
                  templateListQuery?.data?.length > 0 && (
                    <>
                      <TableHead headersProps={["Template Name", "Actions"]} />
                      <TemplateTable
                        list={templateListQuery.data}
                        onPreview={onPreview}
                        onDelete={onDelete}
                      />
                    </>
                  )}
                {!templateListQuery.isLoading &&
                  templateListQuery?.data?.length === 0 && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      py={10}
                    >
                      <Text>No templates found.</Text>
                    </Box>
                  )}
              </Table>
              {!templateListQuery.isLoading && templateListQuery?.data?.length > 0 && (
                <Pagination
                  totalCount={totalCount}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                  isDisabled={templateListQuery.isLoading}
                />
              )}
            </TableContainer>
          </Box>
          <Box p={3}>
            {!templatePreviewBody && (
              <Box
                mb={3}
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="100%"
                height="100%"
              >
                <Text>No Template is selected for preview.</Text>
              </Box>
            )}
            {!!templatePreviewBody && (
              <Box
                mb={3}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Box width="90%">
                  <Box display="flex">
                    <Text as="b" width="100px">
                      Subject Line:
                    </Text>
                    <Text
                      ml={3}
                      overflow="hidden"
                      whiteSpace="nowrap"
                      textOverflow="ellipsis"
                      width="400px"
                      maxW="400px"
                      title={subjectLine}
                    >
                      {subjectLine}
                    </Text>
                  </Box>
                  {!!preheaderText && (
                    <Box display="flex">
                      <Text as="b">Preheader:</Text>
                      <Text ml={3}>{preheaderText}</Text>
                    </Box>
                  )}
                </Box>
                <Icon
                  as={MdOutlineZoomOutMap}
                  boxSize={5}
                  cursor="pointer"
                  color="primary.main"
                  visibility="hidden"
                />
              </Box>
            )}
            <iframe
              id="iframeDesktop"
              frameBorder="0"
              width="100%"
              height="100%"
            />
          </Box>
        </Grid>
      </Box>
    </Fragment>
  );
}